import { useState } from 'react';
import { Tooltip } from 'antd';
import { toast } from 'sonner';
import { faCalendarPlus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthContext';
import { useHomeScope } from '../../hooks/useHomeScope';
import { useMaintenance } from '../../queries/maintenance';
import { usePhone } from '../../hooks/usePhone';
import { useAgentShortName } from '../../queries/agents';
import { Btn, Chip, cx } from '../Todos/ui';
import TicketDrawer from '../Maintenance/TicketDrawer';
import TicketFormModal from '../Maintenance/TicketFormModal';

const NO_ROWS = [];
const FAM = { danger: 'bad', warning: 'warn', orange: 'warn', success: 'ok', info: 'info', cyan: 'info', purple: 'purple' };
const fam = (tone) => FAM[tone] || 'neutral';

export default function MaintenanceTab() {
  const phone = usePhone();
  const agent = useAgentShortName();
  const { can } = useAuth();
  const canManage = can('manage_maintenance');
  const { params } = useHomeScope();
  const { data, isLoading } = useMaintenance(params);
  const tickets = data?.tickets || NO_ROWS;
  const [ticketId, setTicketId] = useState(null);
  const [creating, setCreating] = useState(false);

  const schedule = (
    <Btn kind="primary" sm icon={faPlus} onClick={() => setCreating(true)} disabled={!canManage}>
      Add maintenance request
    </Btn>
  );

  const schedBtn = () => (
    <Tooltip title="Coming soon">
      <span>
        <Btn
          sm
          icon={faCalendarPlus}
          onClick={(e) => {
            e.stopPropagation();
            toast.error('Maintenance scheduling is coming soon.');
          }}
        >
          Schedule maintenance
        </Btn>
      </span>
    </Tooltip>
  );

  const body = phone ? (
    <div className="m-col gap8">
      {tickets.map((t) => (
        <div key={t.id} role="button" tabIndex={0} className="m-crow" onClick={() => setTicketId(t.id)} onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && setTicketId(t.id)}>
          <div className="m-row between">
            <span className="b sm">{t.number || t.issue}</span>
            <Chip fam={fam(t.tierTone)}>{t.tier}</Chip>
          </div>
          <div className="m-sub">
            {t.property} · {t.issue}
          </div>
          <div className="m-row wrap">
            <span className="m-sub grow">
              {t.status} · {t.assigned || 'Unassigned'}
            </span>
            {schedBtn()}
          </div>
        </div>
      ))}
    </div>
  ) : (
    <div className="m-tw">
      <table className="m-tbl">
        <thead>
          <tr>
            {['Number', 'Property or unit', 'Issue', 'Priority', 'State', 'Assigned', ''].map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tickets.length ? (
            tickets.map((t) => (
              <tr key={t.id} className="click" tabIndex={0} onClick={() => setTicketId(t.id)} onKeyDown={(e) => e.key === 'Enter' && setTicketId(t.id)}>
                <td>
                  <span className="b">{t.number || '--'}</span>
                </td>
                <td>{t.property || '--'}</td>
                <td>{t.issue}</td>
                <td>
                  <Chip fam={fam(t.tierTone)}>{t.tier}</Chip>
                </td>
                <td>
                  <Chip fam={fam(t.statusTone)}>{t.status}</Chip>
                </td>
                <td>{t.assigned || 'Unassigned'}</td>
                <td>{schedBtn()}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} className="m-empty">
                {isLoading ? 'Loading work orders…' : 'No work orders yet.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className={cx('m-col', 'gap12')}>
      <div className={phone ? 'm-col' : 'm-row wrap'}>
        <div className="m-sub grow">
          {tickets.length} work orders · {agent} logs new ones from calls and texts
        </div>
        {schedule}
      </div>
      {body}
      <TicketDrawer ticket={tickets.find((t) => t.id === ticketId)} onClose={() => setTicketId(null)} />
      {creating && <TicketFormModal onClose={() => setCreating(false)} />}
    </div>
  );
}
